import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminHeader from '../../components/admin/AdminHeader';
import { supabase } from '../../integrations/supabase/client';

const { FiMail, FiLayout, FiSettings, FiUsers, FiFilter, FiDownload, FiSearch, FiRefreshCw } = FiIcons;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
    fetchSubmissions();
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin');
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSubmissions(data);
    }
    setLoading(false);
  };

  // CSV Export Function
  const handleExportCSV = () => {
    if (!submissions.length) return;

    // Define headers
    const headers = ['Name', 'Email', 'Phone', 'Subject', 'Message', 'Date'];
    
    // Create rows
    const csvRows = [headers.join(',')];

    for (const row of submissions) {
      const values = [
        `"${row.name || ''}"`,
        `"${row.email || ''}"`,
        `"${row.phone || ''}"`,
        `"${row.subject || ''}"`,
        `"${(row.message || '').replace(/"/g, '""')}"`, // Escape quotes within content
        `"${new Date(row.created_at).toLocaleString()}"`
      ];
      csvRows.push(values.join(','));
    }

    // Create blob and download
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bondeega-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = [
    { 
      title: "Contact Submissions", 
      count: submissions.length, 
      icon: FiMail, 
      active: true,
      link: "/admin/dashboard"
    },
    { 
      title: "Website Customizer", 
      count: null, 
      icon: FiLayout, 
      active: false,
      link: "/admin/customizer" 
    },
    { 
      title: "Job Applications", 
      count: 0, 
      icon: FiUsers, 
      active: false,
      link: "#" 
    },
    { 
      title: "Profile Settings", 
      count: null, 
      icon: FiSettings, 
      active: false,
      link: "#" 
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Admin Dashboard" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <motion.div
                whileHover={{ y: -2 }}
                className={`p-6 rounded-xl border shadow-sm flex items-center justify-between cursor-pointer transition-all ${
                  stat.active 
                    ? 'bg-green-800 text-white border-green-900' 
                    : 'bg-white text-gray-700 border-gray-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  <SafeIcon icon={stat.icon} className="w-5 h-5" />
                  <span className="font-semibold">{stat.title}</span>
                </div>
                {stat.count !== null && (
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    stat.active ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {stat.count}
                  </span>
                )}
              </motion.div>
            </Link>
          ))}
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Toolbar */}
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between gap-4">
            <div className="relative w-full md:w-96">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Search submissions..." 
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-green-500 outline-none bg-gray-50"
              />
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={fetchSubmissions}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button 
                onClick={handleExportCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-sm transition-colors"
              >
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Table Info */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 text-sm text-gray-500">
            Showing {submissions.length} submissions
          </div>

          {/* Table Header */}
          <div className="hidden md:grid bg-green-900 text-white grid-cols-12 gap-4 px-6 py-4 text-xs font-bold uppercase tracking-wider">
            <div className="col-span-2">Name</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-2">Subject</div>
            <div className="col-span-3">Message</div>
            <div className="col-span-2 text-right">Date</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading submissions...</div>
            ) : submissions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No submissions found.</div>
            ) : (
              submissions.map((item) => (
                <motion.div 
                  key={item.id}
                  whileHover={{ backgroundColor: "#f9fafb" }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 text-sm text-gray-700 items-center border-b md:border-b-0"
                >
                  <div className="col-span-2 font-medium">
                    <span className="md:hidden font-bold text-gray-500 block text-xs">Name:</span>
                    {item.name}
                  </div>
                  <div className="col-span-3 text-gray-500 text-xs break-all">
                    <span className="md:hidden font-bold text-gray-500 block text-xs mt-2">Contact:</span>
                    {item.email}<br/>{item.phone}
                  </div>
                  <div className="col-span-2 font-medium truncate">
                    <span className="md:hidden font-bold text-gray-500 block text-xs mt-2">Subject:</span>
                    {item.subject || '-'}
                  </div>
                  <div className="col-span-3 text-gray-500 truncate">
                    <span className="md:hidden font-bold text-gray-500 block text-xs mt-2">Message:</span>
                    {item.message}
                  </div>
                  <div className="col-span-2 text-right text-gray-500 text-xs">
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;